import { useIsAdmin, useAdminUsers } from '@/hooks/use-admin';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield, Loader2, UserCog, Gift, Users, Trophy, MessageSquare, Trash2, ArrowUpDown, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

const TIER_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'pro', label: 'Pro' },
  { value: 'lifetime', label: 'Premium Lifetime' },
  { value: 'founding_member', label: 'Founding Member' },
];

const TIER_COLORS: Record<string, string> = {
  free: 'bg-muted text-muted-foreground',
  pro: 'bg-primary/10 text-primary',
  lifetime: 'bg-accent text-accent-foreground',
  founding_member: 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 text-amber-700 dark:text-amber-400',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-muted text-muted-foreground',
  signed_up: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  rewarded: 'bg-green-500/10 text-green-700 dark:text-green-400',
};

interface ReferralData {
  referrals: any[];
  rewards: any[];
  nameMap: Record<string, string | null>;
}

function ReferralDashboard() {
  const [data, setData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supabase.functions.invoke('admin-update-tier', { body: { action: 'referrals' } });
      if (res.error) throw res.error;
      setData(res.data);
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to load referrals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReferrals(); }, [fetchReferrals]);

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (!data) return null;

  const totalReferrals = data.referrals.length;
  const rewarded = data.referrals.filter(r => r.status === 'rewarded').length;
  const pending = data.referrals.filter(r => r.status === 'signed_up').length;
  const totalRewards = data.rewards.length;
  const appliedRewards = data.rewards.filter(r => r.applied).length;
  const getName = (id: string) => data.nameMap[id] ?? id.slice(0, 8) + '…';

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Referrals', value: totalReferrals, icon: Users },
          { label: 'Rewarded', value: rewarded, icon: Trophy },
          { label: 'Pending', value: pending, icon: Users },
          { label: 'Rewards Issued', value: `${appliedRewards}/${totalRewards}`, icon: Gift },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 text-center">
              <s.icon className="mx-auto mb-1 h-4 w-4 text-primary" />
              <p className="font-display text-xl font-bold">{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Referrals table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">All Referrals ({totalReferrals})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-2 font-medium">Referrer</th>
                  <th className="text-left px-4 py-2 font-medium">Referred</th>
                  <th className="text-left px-4 py-2 font-medium">Code</th>
                  <th className="text-left px-4 py-2 font-medium">Status</th>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">No referrals yet</td></tr>
                ) : data.referrals.map(r => (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-medium">{getName(r.referrer_id)}</td>
                    <td className="px-4 py-3">{r.referred_id ? getName(r.referred_id) : <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.referral_code}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={STATUS_COLORS[r.status] ?? ''}>{r.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Rewards table */}
      {data.rewards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All Rewards ({totalRewards})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-2 font-medium">User</th>
                    <th className="text-left px-4 py-2 font-medium">Type</th>
                    <th className="text-left px-4 py-2 font-medium">Applied</th>
                    <th className="text-left px-4 py-2 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rewards.map(rw => (
                    <tr key={rw.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{getName(rw.user_id)}</td>
                      <td className="px-4 py-3 text-xs">{rw.reward_type}</td>
                      <td className="px-4 py-3">
                        <Badge variant={rw.applied ? 'secondary' : 'default'} className="text-[10px]">
                          {rw.applied ? 'Applied' : 'Available'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(rw.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  bug: 'Bug Report',
  feature: 'Feature Request',
  billing: 'Billing',
  'bank-sync': 'Bank Sync',
};

const CATEGORY_COLORS: Record<string, string> = {
  general: 'bg-muted text-muted-foreground',
  bug: 'bg-destructive/10 text-destructive',
  feature: 'bg-primary/10 text-primary',
  billing: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  'bank-sync': 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
};

const FEEDBACK_STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const FEEDBACK_STATUS_COLORS: Record<string, string> = {
  open: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  'in-progress': 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
  resolved: 'bg-green-500/10 text-green-700 dark:text-green-400',
};

interface FeedbackItem {
  id: string;
  user_id: string;
  user_email: string | null;
  subject: string;
  message: string;
  category: string;
  status: string;
  created_at: string;
}

function FeedbackDashboard() {
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest'>('newest');

  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setItems((data as any[]) ?? []);
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (filterStatus !== 'all') result = result.filter(i => i.status === filterStatus);
    if (filterCategory !== 'all') result = result.filter(i => i.category === filterCategory);
    if (sortBy === 'oldest') result = [...result].reverse();
    return result;
  }, [items, filterStatus, filterCategory, sortBy]);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      const { error } = await supabase.from('feedback').delete().eq('id', id);
      if (error) throw error;
      setItems(prev => prev.filter(i => i.id !== id));
      toast.success('Feedback deleted');
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    setUpdatingStatus(id);
    try {
      const { error } = await supabase.from('feedback').update({ status } as any).eq('id', id);
      if (error) throw error;
      setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
      toast.success('Status updated');
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const openCount = items.filter(i => i.status === 'open').length;
  const inProgressCount = items.filter(i => i.status === 'in-progress').length;
  const resolvedCount = items.filter(i => i.status === 'resolved').length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total', value: items.length },
          { label: 'Open', value: openCount },
          { label: 'In Progress', value: inProgressCount },
          { label: 'Resolved', value: resolvedCount },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="p-3 text-center">
              <p className="font-display text-xl font-bold">{s.value}</p>
              <p className="text-[10px] text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & sort */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Filter className="h-3.5 w-3.5" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[120px] h-8 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {FEEDBACK_STATUS_OPTIONS.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
              <SelectItem key={val} value={val}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={() => setSortBy(s => s === 'newest' ? 'oldest' : 'newest')}
        >
          <ArrowUpDown className="h-3.5 w-3.5" />
          {sortBy === 'newest' ? 'Newest first' : 'Oldest first'}
        </Button>
        {(filterStatus !== 'all' || filterCategory !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => { setFilterStatus('all'); setFilterCategory('all'); }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {filteredItems.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {items.length === 0 ? 'No feedback submissions yet' : 'No feedback matches the selected filters'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">{filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''}</p>
          {filteredItems.map(item => (
            <Card key={item.id} className="overflow-hidden">
              <CardContent className="p-0">
                <button
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className={CATEGORY_COLORS[item.category] ?? ''}>
                        {CATEGORY_LABELS[item.category] ?? item.category}
                      </Badge>
                      <Badge variant="outline" className={FEEDBACK_STATUS_COLORS[item.status] ?? ''}>
                        {FEEDBACK_STATUS_OPTIONS.find(s => s.value === item.status)?.label ?? item.status}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="font-medium text-sm truncate">{item.subject}</p>
                    <p className="text-xs text-muted-foreground truncate">{item.user_email ?? item.user_id.slice(0, 8) + '…'}</p>
                  </div>
                </button>

                {expandedId === item.id && (
                  <div className="border-t px-4 py-3 space-y-3 bg-muted/20">
                    <p className="text-sm whitespace-pre-wrap">{item.message}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={item.status}
                        onValueChange={(v) => handleStatusChange(item.id, v)}
                        disabled={updatingStatus === item.id}
                      >
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FEEDBACK_STATUS_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {item.user_email && (
                        <Button asChild variant="outline" size="sm" className="gap-1.5">
                          <a href={`mailto:${item.user_email}?subject=Re: ${encodeURIComponent(item.subject)}`}>
                            Reply
                          </a>
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        className="gap-1.5"
                        disabled={deleting === item.id}
                        onClick={() => handleDelete(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        {deleting === item.id ? 'Deleting…' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { users, loading, error, updateTier } = useAdminUsers();
  const [updating, setUpdating] = useState<string | null>(null);

  if (roleLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 text-muted-foreground">
        <Shield className="h-12 w-12" />
        <p className="text-lg font-semibold">Access Denied</p>
        <p className="text-sm">You don't have permission to view this page.</p>
      </div>
    );
  }

  const handleTierChange = async (userId: string, tier: string) => {
    setUpdating(userId);
    try {
      await updateTier(userId, tier);
      toast.success('Tier updated successfully');
    } catch (e: any) {
      toast.error(e.message ?? 'Failed to update tier');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-6">
      <div className="flex items-center gap-3">
        <UserCog className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">Administration</h1>
      </div>

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users" className="gap-1.5"><UserCog className="h-3.5 w-3.5" /> Users</TabsTrigger>
          <TabsTrigger value="feedback" className="gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Feedback</TabsTrigger>
          <TabsTrigger value="referrals" className="gap-1.5"><Gift className="h-3.5 w-3.5" /> Referrals</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-4">
          {error && (
            <Card className="border-destructive mb-4">
              <CardContent className="p-4 text-sm text-destructive">{error}</CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">All Users ({users.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-4 py-2 font-medium">User</th>
                      <th className="text-left px-4 py-2 font-medium">Current Tier</th>
                      <th className="text-left px-4 py-2 font-medium">Change Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => {
                      const isPaidViaStripe = !!user.stripe_customer_id && user.subscription_tier !== 'free';
                      return (
                      <tr key={user.user_id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-medium">{user.display_name ?? 'Unnamed'}</p>
                          <p className="text-xs text-muted-foreground">{user.email ?? user.user_id}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={TIER_COLORS[user.subscription_tier] ?? ''}>
                            {TIER_OPTIONS.find(t => t.value === user.subscription_tier)?.label ?? user.subscription_tier}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {isPaidViaStripe ? (
                            <p className="text-xs text-muted-foreground italic">Managed by Stripe</p>
                          ) : (
                          <Select
                            value={user.subscription_tier}
                            onValueChange={(v) => handleTierChange(user.user_id, v)}
                            disabled={updating === user.user_id}
                          >
                            <SelectTrigger className="w-[180px] h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {TIER_OPTIONS.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="feedback" className="mt-4">
          <FeedbackDashboard />
        </TabsContent>

        <TabsContent value="referrals" className="mt-4">
          <ReferralDashboard />
        </TabsContent>
      </Tabs>
    </div>
  );
}
